import {
    Box,
    OrbitControls,
    PerspectiveCamera,
    useHelper,
} from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
//import * as geolib from "geolib";
import * as geolib from 'geolib';

import { useRef, useState } from 'react';
import * as THREE from 'three';

import {
    V3D_Block,
    V3D_Floor,
    V3D_Object,
    V3D_Temperatures,
    V3D_Template,
} from './types';

import './chart.css';
import { GradientKey } from 'src/models/GradientKey';
import {
    generateGradientKey,
    getGradientColorForTemperature,
    gradients,
} from 'src/utils/gradients';

interface ChartProps {
    objects: V3D_Object[];
    onSensorSelected: (id: number, selected: boolean) => void;
    selectedSensors: number[];
    referenceTemperature?: number;
    tolerance?: number;
    onPointerOver: (id: number, leave: boolean) => void;
    continuousSpin: boolean;
    onObjectPointerOver?: (object?: V3D_Object) => void;
    gradientKey?: GradientKey;
}

function geoToXY(
    origin: { longitude: number; latitude: number },
    target: { longitude: number; latitude: number },
) {
    const accuracy = 0.001;

    const xDistance = geolib.getPreciseDistance(
        origin,
        {
            latitude: origin.latitude,
            longitude: target.longitude,
        },
        accuracy,
    );

    const yDistance = geolib.getPreciseDistance(
        origin,
        {
            latitude: target.latitude,
            longitude: origin.longitude,
        },
        accuracy,
    );

    const x = target.longitude > origin.longitude ? xDistance : -xDistance;
    const y = target.latitude > origin.latitude ? yDistance : -yDistance;

    return { x, y };
}

function calcAngleWithGeo(
    start: { longitude: number; latitude: number },
    stop: { longitude: number; latitude: number },
) {
    //x is adjacent and y is opposite
    //x and y together specifies which quadrant
    const { x, y } = geoToXY(start, stop);

    let result = Math.atan(y / x);

    if (result < 0) {
        result += Math.PI / 2;
    }

    if (x < 0 && y >= 0) {
        result += Math.PI / 2;
    } else if (x < 0 && y < 0) {
        result += Math.PI;
    } else if (x >= 0 && y < 0) {
        result += Math.PI + Math.PI / 2;
    }

    return result;
}

const RenderFloorModel = (props: {
    templates: V3D_Template[];
    averageTemperatures: V3D_Temperatures[];
    block: V3D_Block;
    floor: V3D_Floor;
    floorTotHeight: number;
    center: { latitude: number; longitude: number };
    referenceTemperature: number;
    selectedSensors: number[];
    tolerance?: number;
    gradientKey: GradientKey;
    onSensorSelected: (id: number, selected: boolean) => void;
    onPointerOver: (id: number, leave: boolean) => void;
}) => {
    const {
        templates,
        block,
        floor,
        floorTotHeight,
        center,
        averageTemperatures,
        referenceTemperature,
        onSensorSelected,
        onPointerOver,
        selectedSensors,
        tolerance,
        gradientKey,
    } = props;

    //get width in meters
    const blockLength = geolib.getPreciseDistance(
        { latitude: block.leftCornerLat, longitude: block.leftCornerLong },
        { latitude: block.rightCornerLat, longitude: block.rightCornerLong },
        0.1,
    );

    //get correct template
    const template = templates.find(
        (template) => template.templateId === floor.templateId,
    );

    //get X and Y coordinates in meters
    const { x, y } = geoToXY(center, {
        latitude: block.leftCornerLat,
        longitude: block.leftCornerLong,
    });

    // console.log("x and y :", x, y, floorTotHeight, "block.id",block.id);

    //get angle of house in radians
    //	borde min vinkel utgå från centrum på något sätt?
    const angle = calcAngleWithGeo(
        { latitude: block.leftCornerLat, longitude: block.leftCornerLong },
        { latitude: block.rightCornerLat, longitude: block.rightCornerLong },
    );

    // console.log("angle", angle, block.id);

    // const angleTest = calcAngleWithXY(
    // 	{ x: x, y: y },
    // 	geoToXY(center, {
    // 		latitude: block.rightCornerLat,
    // 		longitude: block.rightCornerLong,
    // 	})
    // );
    // console.log("angles", angleTest, angle, block.id);
    //fake depth in meters

    // console.log("dimensions", block.id, blockLength, block.width);

    const opacity = selectedSensors.length === 0 ? 0.3 : 0.2;

    return (
        <group
            onPointerOver={() => {}}
            onPointerOut={() => {}}
            position={[x, block.zOffset, -y]}
            rotation={[0, angle, 0]}
            key={block.id}
        >
            <mesh>
                {template!.spaces.map((space) => {
                    const spaceSizeX = space.sizeX * blockLength;
                    const spacePosX = space.posX * blockLength;
                    const spaceSizeY = space.sizeY * block.width;
                    const spacePosY = space.posY * block.width;

                    const datapoint = block.datapoints?.find(
                        (datapoint) =>
                            datapoint.floorId === floor.floorId &&
                            datapoint.spaceNumber === space.spaceNumber,
                    );
                    return (
                        <Box
                            onPointerOver={(ev) => {
                                if (datapoint?.id)
                                    onPointerOver(datapoint?.id, true);
                            }}
                            onPointerLeave={(ev) => {
                                ev.stopPropagation();
                                if (datapoint?.id)
                                    onPointerOver(datapoint?.id, false);
                            }}
                            args={[
                                // Small delta removes flimmer due to overlap
                                spaceSizeX - 0.001,
                                floor.height - 0.001,
                                spaceSizeY - 0.001,
                            ]}
                            position={[
                                spacePosX + spaceSizeX / 2,
                                floorTotHeight + floor.height / 2,
                                -(spacePosY + spaceSizeY / 2),
                            ]}
                            castShadow
                            onClick={(ev) => {
                                if (datapoint?.id) {
                                    onSensorSelected(
                                        datapoint.id,
                                        // If it is included we remove it
                                        !selectedSensors.includes(datapoint.id),
                                    );
                                    ev.stopPropagation();
                                }
                            }}
                            key={Math.random()}
                        >
                            <meshLambertMaterial
                                side={THREE.DoubleSide}
                                color={
                                    space.spaceNumber === 0
                                        ? 0x000000
                                        : datapoint
                                        ? getGradientColorForTemperature(
                                              datapoint.temperatures,
                                              averageTemperatures,
                                              referenceTemperature,
                                              gradientKey,
                                              tolerance,
                                          )
                                        : 0xffffff
                                }
                                attach="material"
                                transparent={true}
                                opacity={
                                    selectedSensors.includes(datapoint?.id!)
                                        ? 1
                                        : opacity
                                }
                            />
                        </Box>
                    );
                })}
            </mesh>
        </group>
    );
};

function add(...nums: number[]) {
    let i = 0;
    for (const v of nums) i += v;

    return i;
}

function ChartObject(props: {
    object: V3D_Object;
    referenceTemperature?: number;
    selectedSensors: number[];
    tolerance?: number;
    center: any;
    gradientKey: GradientKey;
    onSensorSelected: (id: number, selected: boolean) => void;
    onPointerOver: (id: number, leave: boolean) => void;
    onObjectPointerOver?: (object?: V3D_Object) => void;
}) {
    const {
        object,
        referenceTemperature,
        onSensorSelected,
        selectedSensors,
        onPointerOver,
        onObjectPointerOver,
        tolerance,
        center,
        gradientKey,
    } = props;
    //get helping axises
    const cubeRef = useRef<any>();
    useHelper(cubeRef, THREE.BoxHelper, 'blue');

    const reference =
        referenceTemperature ??
        object.referenceTemperatures.pseudoDOT ??
        object.referenceTemperatures.dot ??
        0;

    //Use this to calibrate camera?
    // console.log("bounds: ", geolib.getBounds(latAndLong));
    //center of bounds
    // console.log("center: ", geolib.getCenterOfBounds(latAndLong));

    return (
        <>
            {reference !== undefined &&
                object.blocks?.map((block) => {
                    return (
                        <group
                            onPointerOver={() => {
                                if (onObjectPointerOver) {
                                    onObjectPointerOver(object);
                                }
                            }}
                            onPointerOut={() => {
                                if (onObjectPointerOver) {
                                    onObjectPointerOver(undefined);
                                }
                            }}
                            key={block.id}
                        >
                            <mesh>
                                {block.floors?.map((floor, index) => {
                                    return (
                                        <RenderFloorModel
                                            onPointerOver={onPointerOver}
                                            selectedSensors={selectedSensors}
                                            onSensorSelected={onSensorSelected}
                                            floorTotHeight={add(
                                                ...block.floors
                                                    .slice(0, index)
                                                    .map(
                                                        (floor) => floor.height,
                                                    ),
                                            )}
                                            floor={floor}
                                            templates={object.templates}
                                            block={block}
                                            center={center}
                                            averageTemperatures={
                                                object.averageTemperatures
                                            }
                                            referenceTemperature={Math.round(
                                                reference,
                                            )}
                                            tolerance={tolerance}
                                            gradientKey={gradientKey}
                                            key={floor.floorId}
                                        />
                                    );
                                })}
                            </mesh>
                        </group>
                    );
                })}
        </>
    );
}

function Camera(props: { spin: boolean; numberOfBlocks: number }) {
    const { spin, numberOfBlocks } = props;
    const [spinRotation, setSpinRotation] = useState(0);

    const camera = useRef<any>();

    useFrame(() => {
        if (camera.current) {
            if (camera.current.position.y < 10) camera.current.position.y = 10;
            if (spin) {
                camera.current.position.x = 100 * Math.cos(spinRotation);
                camera.current.position.z = -100 * Math.sin(spinRotation);
                setSpinRotation((spinRotation + 0.001) % (2 * Math.PI));
            }
        }
    });

    return (
        <PerspectiveCamera
            makeDefault
            ref={camera}
            fov={numberOfBlocks > 2 ? 40 : 20}
            args={[45, 10, 1, 10000]}
            position={[100, 40, 0]}
        />
    );
}

function Chart(props: ChartProps) {
    const {
        objects,
        referenceTemperature,
        onPointerOver,
        onSensorSelected,
        onObjectPointerOver,
        selectedSensors,
        tolerance,
        continuousSpin,
        gradientKey = generateGradientKey(
            { high: 22, low: 20 },
            gradients.thermometer.mix,
        ),
    } = props;

    //Get coordinates to determine the center
    let latAndLong: any = [];
    objects.forEach((object) => {
        latAndLong = [
            ...latAndLong,
            ...object.blocks.flatMap((block) => {
                return [
                    {
                        latitude: block.leftCornerLat,
                        longitude: block.leftCornerLong,
                    },
                    {
                        latitude: block.rightCornerLat,
                        longitude: block.rightCornerLong,
                    },
                ];
            }),
        ];
    });

    const center = geolib.getCenterOfBounds(latAndLong);

    function calculateTotalBlocks(objects: V3D_Object[]) {
        return objects.reduce((totalBlocks, object) => {
            // Assuming each object has a 'blocks' property that contains the number of blocks
            if (object.blocks !== undefined && !isNaN(object?.blocks.length)) {
                return totalBlocks + object.blocks.length;
            }
            return totalBlocks;
        }, 0);
    }

    return (
        <>
            <Canvas
                gl={{ preserveDrawingBuffer: true }}
                style={{
                    width: '100%',
                    height: '100%',
                }}
            >
                {/* <primitive object={new THREE.AxesHelper(500)} /> */}
                {/* Change camera to one that can walk around? */}
                <Camera
                    spin={continuousSpin}
                    numberOfBlocks={calculateTotalBlocks(objects)}
                />
                <OrbitControls makeDefault enableRotate enableZoom />
                <ambientLight intensity={0.2} />
                <pointLight position={[500, 500, 500]} />
                {/* Renders the objects */}

                {/* <Box args={[1,1,1]} position={[0, 0.5, 0]}></Box> */}
                {objects.map((object) => {
                    return (
                        <ChartObject
                            onObjectPointerOver={onObjectPointerOver}
                            onPointerOver={onPointerOver}
                            onSensorSelected={onSensorSelected}
                            object={object}
                            center={center}
                            selectedSensors={selectedSensors}
                            referenceTemperature={referenceTemperature}
                            tolerance={tolerance}
                            gradientKey={gradientKey}
                            key={object.objectId}
                        />
                    );
                })}
            </Canvas>
        </>
    );
}

export default Chart;
